import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Paper, Typography, Box, Grid, useTheme, alpha, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import {
  AlarmOff as NoReminderIcon,
  AssignmentLate as UntouchedIcon,
  PersonOff as UnassignedIcon,
  PendingActions as PendingFollowupIcon,
  Tour as PendingVisitIcon,
  SnoozeOutlined as DormantIcon,
  Flag as FlaggedIcon,
  Warning as HotWarmAtRiskIcon,
} from '@mui/icons-material';

// Define stat meta for pending action cards
const pendingActionMeta = [
  {
    key: 'unassigned_leads',
    title: 'Unassigned Leads',
    icon: <UnassignedIcon />,
    color: '#64748b',
    stat_type: 'unassigned-leads',
    filters: { assigned_agent_id__isnull: true },
    description: 'Leads without an owner',
    showTrend: false
  },
  {
    key: 'untouched_leads',
    title: 'Untouched Leads',
    icon: <UntouchedIcon />,
    color: '#f97316',
    stat_type: 'untouched-leads',
    filters: { last_contacted_at__isnull: true },
    description: 'No outreach logged yet',
    showTrend: false
  },
  {
    key: 'no_reminder_leads',
    title: 'Leads with No Reminder',
    icon: <NoReminderIcon />,
    color: '#facc15',
    stat_type: 'no-reminder-leads',
    filters: { followup_at__isnull: true },
    description: 'Missing scheduled reminders',
    showTrend: false
  },
  {
    key: 'pending_followup_leads',
    title: 'Pending Followups',
    icon: <PendingFollowupIcon />,
    color: '#a855f7',
    stat_type: 'pending-followup-leads',
    filters: {},
    description: 'Leads where follow-up is overdue',
    showTrend: false
  },
  {
    key: 'pending_visit_leads',
    title: 'Pending Visits',
    icon: <PendingVisitIcon />,
    color: '#22c55e',
    stat_type: 'pending-visit-leads',
    filters: {},
    description: 'Leads awaiting scheduled site visits',
    showTrend: false
  },
  {
    key: 'dormant_leads',
    title: 'Dormant Leads',
    icon: <DormantIcon />,
    color: '#6b7280',
    stat_type: 'dormant-leads',
    filters: { stage: ['new', 'contacted', 'follow_up'] },
    description: 'Active stage leads with no activity in last 15 days',
    showTrend: false
  },
  {
    key: 'hot_warm_at_risk',
    title: 'Hot/Warm Leads at Risk',
    icon: <HotWarmAtRiskIcon />,
    color: '#f59e0b',
    stat_type: 'hot-warm-at-risk',
    filters: {},
    description: 'Hot/Warm tagged leads inactive for 15+ days',
    showTrend: false
  },
  {
    key: 'flagged_leads',
    title: 'Flagged Leads',
    icon: <FlaggedIcon />,
    color: '#ef4444',
    stat_type: 'flagged-leads',
    filters: { status: 'not_interested' },
    description: 'Lead marked not interested without any connected calls',
    showTrend: false
  },
];

const sampleStats = {
  untouched_leads: { value: 32, trending: 'up', change: { value: '+2%', label: 'vs last week' } },
  no_reminder_leads: { value: 24, trending: 'down', change: { value: '-6%', label: 'vs last week' } },
  pending_followup_leads: { value: 0, trending: 'down', change: { value: '-3%', label: 'vs last week' } },
  pending_visit_leads: { value: 0, trending: 'stable', change: { value: '0%', label: 'vs last week' } },
  unassigned_leads: { value: 18, trending: 'down', change: { value: '-3%', label: 'vs last week' } },
  dormant_leads: { value: 9, trending: 'down', change: { value: '-2%', label: 'vs last week' } },
  hot_warm_at_risk: { value: 5, trending: 'stable', change: { value: '0%', label: 'vs last week' } },
  flagged_leads: { value: 0, trending: 'stable', change: { value: '0%', label: 'vs last week' } },
};

const StatCard = ({ title, value, change, icon, color, trending, filters, description, metaKey, statType, showTrend = true, selectedTeams = [], selectedAgents = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Determine trend color and icon
  const getTrendColor = () => {
    if (trending === 'up') return theme.palette.success.main;
    if (trending === 'down') return theme.palette.error.main;
    return theme.palette.grey[500]; // stable
  };

  const getTrendIcon = () => {
    if (trending === 'up') return '↑';
    if (trending === 'down') return '↓';
    return '→'; // stable
  };

  const handleCardClick = () => {
    if (!statType) return;
    
    // Build query parameters - no date range needed for pending actions
    const params = new URLSearchParams();
    params.set('stat_type', statType);
    
    // Get current dashboard filter from URL if available
    const currentUrl = new URL(window.location.href);
    const filterId = currentUrl.searchParams.get('filter_id');
    const startDate = currentUrl.searchParams.get('start_date');
    const endDate = currentUrl.searchParams.get('end_date');
    
    // Add team filter if provided
    if (selectedTeams && selectedTeams.length > 0) {
      params.set('teams', selectedTeams.join(','));
    }
    
    // Add agent filter if provided
    if (selectedAgents && selectedAgents.length > 0) {
      params.set('agents', selectedAgents.join(','));
    }
    
    // Navigate to leads page with state to remember dashboard filter
    navigate(`/leads?${params.toString()}`, {
      state: { 
        from: 'dashboard',
        dashboardFilter: filterId,
        dashboardStartDate: startDate,
        dashboardEndDate: endDate,
        selectedTeams: selectedTeams,
        selectedAgents: selectedAgents
      }
    });
  };

  return (
    <Paper
      elevation={0}
      onClick={handleCardClick}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.2)}`,
        background: 'background.paper',
        boxShadow: `0 2px 8px ${alpha(color, 0.08)}`,
        transition: 'all 0.3s ease',
        height: '100%',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: `0 4px 16px ${alpha(color, 0.15)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: color,
              mb: 0.5,
              fontSize: '1.75rem',
            }}
          >
            {value}
          </Typography>
          {showTrend && change && (
            <Typography
              variant="body2"
              sx={{
                color: getTrendColor(),
                fontSize: '0.75rem',
                mt: 1,
              }}
            >
              <Box component="span" sx={{ mr: 0.5, fontSize: '0.875rem' }}>
                {getTrendIcon()}
              </Box>
              {change.value}
              {change.label && (
                <span style={{ marginLeft: 6, color: theme.palette.text.secondary }}>
                  vs {change.label}
                </span>
              )}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(color, 0.1),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );
};

// Map raw API response to stats (shared logic for external and internal fetch)
const mapResponseToStats = (response) => {
  if (!response) return sampleStats;
  return Object.keys(sampleStats).reduce((acc, key) => {
    const sampleStat = sampleStats[key] || { value: '--' };
    const backendStat = response[key];
    if (backendStat && typeof backendStat === 'object' && !Array.isArray(backendStat)) {
      acc[key] = {
        ...sampleStat,
        ...backendStat,
        change: backendStat.change ? { ...sampleStat.change, ...backendStat.change } : sampleStat.change,
      };
    } else if (typeof backendStat === 'number') {
      acc[key] = { ...sampleStat, value: backendStat };
    } else {
      acc[key] = sampleStat;
    }
    return acc;
  }, {});
};

const PendingActionStats = ({ dateRangeState, getPeriodType, selectedTeams = [], selectedAgents = [], externalPendingResponse, externalPendingLoading }) => {
  const theme = useTheme();
  const { fetchPendingActions, resetDuplicateCallPrevention } = useDashboard();
  const [stats, setStats] = useState(sampleStats);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef('');

  const useExternalData = typeof externalPendingResponse !== 'undefined';

  // When parent provides data, derive stats from it (no internal fetch)
  const effectiveStats = useExternalData ? mapResponseToStats(externalPendingResponse) : stats;
  const effectiveLoading = useExternalData ? externalPendingLoading : loading;

  // Reset fetch tracking when component mounts (only when not using external data)
  useEffect(() => {
    if (useExternalData) return;
    lastFetchRef.current = '';
    if (resetDuplicateCallPrevention) resetDuplicateCallPrevention();
  }, [useExternalData, resetDuplicateCallPrevention]);

  useEffect(() => {
    if (useExternalData) return;

    const teamFilterKey = selectedTeams.join(',');
    const agentFilterKey = selectedAgents.join(',');
    const fetchKey = `pending_actions_${teamFilterKey}_${agentFilterKey}`;
    if (fetchKey === lastFetchRef.current) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchPendingActions(selectedTeams, selectedAgents);
        if (response) {
          setStats(mapResponseToStats(response));
        } else {
          setStats(sampleStats);
        }
        lastFetchRef.current = fetchKey;
      } catch (e) {
        console.error('PendingActionStats: Error fetching pending actions:', e);
        setStats(sampleStats);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [useExternalData, selectedTeams, selectedAgents, fetchPendingActions]);

  // Skeleton UI component for loading state
  const SkeletonCard = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        background: 'background.paper',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={40} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="50%" height={16} sx={{ mt: 1 }} />
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
      </Box>
    </Paper>
  );

  if (effectiveLoading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <SkeletonCard />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {pendingActionMeta.map((meta) => {
        const stat = effectiveStats[meta.key];
        return (
          <Grid item xs={12} sm={6} md={3} key={meta.key}>
            <StatCard
              title={meta.title}
              value={stat?.value ?? '--'}
              icon={meta.icon}
              color={meta.color}
              trending={stat?.trending}
              change={stat?.change}
              showTrend={meta.showTrend}
              filters={meta.filters}
              description={meta.description}
              metaKey={meta.key}
              statType={meta.stat_type}
              selectedTeams={selectedTeams}
              selectedAgents={selectedAgents}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default React.memo(PendingActionStats);

